import { Component, HostListener, OnInit, Optional, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ClientService } from '../../_services';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { ClientListing } from '../../_models/clients/client-listing.model';
import { ClientListForAddStudentResponse } from '../../_models/clients/client-list-for-add-student-response.model';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { StudentFeedbackService } from '../../_services/student-feedback/student-feedback.service';
import { SearchFeedbackDashboardData } from '../../_models/feedback-dashboard/search-feedback-dashboard-data.model';
import { FeedbackDashboard, FeedbackDashboardItem } from '../../_models/feedback-dashboard/feedback-dashboard.model';
import { DashboardMainItemSectionComponent } from './sections/dashboard-main-item-section/dashboard-main-item-section.component';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { ReportService } from '../../_services/report.service';
import { ReportTutorFilter } from '../../_models/reports/report-tutor-filter.model';

@Component({
    selector: 'student-feedback-dashboard',
    templateUrl: './student-feedback-dashboard.component.html',
    styleUrls: ['./student-feedback-dashboard.component.css'],
    standalone: false
})
export class StudentFeedbackDashboardComponent implements OnInit {
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;


  public feedbackSurveys: Array<FeedbackDashboardItem> = new Array<FeedbackDashboardItem>();

  public selectedClient: ClientListing;
  public clientDisabled = (item: any): boolean => item?.clientId == 0;
  public searchAllClients: boolean = false;
  public clientIds: Array<number> = new Array<number>();
  /**
   * The filters.feedback-dashboard switch. Off, the dashboard is exactly as it was before the filter
   * work: no brand, survey type or tutor filters, and none of their request parameters.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersFeedbackDashboard;
  public filtersEnabled: boolean = false;

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** 'SESSION' or 'TUITION'; empty string is both. */
  public surveyType: string = '';
  /** Tutor (Orbit candidate id); null is every tutor. Offered once a client is chosen. */
  public tutorId: number | null = null;
  public tutorList: ReportTutorFilter[] = [];

  public searchTextPlaceholder: string = "";

  public enhancedSearchPermission: boolean = true;

  public selectedFilterDateRange: string = "";
  // yyyy-MM-dd STRINGS: the native <input type="date"> ngModel reads/writes strings — a Date
  // renders blank and later crashes getFilterDates('custom') on setHours (same fix as the
  // session overview). Defaults: from = one month ago, to = today (end of day in the request).
  public fromDate: string = StudentFeedbackDashboardComponent.defaultFromDate();
  public toDate: string = StudentFeedbackDashboardComponent.defaultToDate();
  public fromDateAfterToDate: boolean = false;

  public clientList: Array<ClientListing> = new Array<ClientListing>();
  public filteredClientList: Array<ClientListing> = new Array<ClientListing>();
  public showClientAccessError: boolean = false;
  public showClientValidationError: boolean = false;

  public initialSearchClicked: boolean = false;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    client: 'number', all: 'boolean', brand: 'number[]', surveyType: 'string', tutor: 'number',
    range: 'string', from: 'string', to: 'string'
  };
  /** Remembered filters and filter state in the URL; used only while filters.feedback-dashboard is on. */
  private filterUx: ScreenFilterUx;
  /** Filters to restore once both the switch and the client list have arrived. */
  private pendingFilterState: FilterState = null;
  private clientsLoaded: boolean = false;

  private static defaultFromDate(): string {
    return new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10);
  }

  private static defaultToDate(): string {
    return new Date().toISOString().slice(0, 10);
  }  public refreshingGraph: boolean = false;

  private _resized = new Subject<void>();

  @HostListener('window:resize', ['$event.target'])
  onResize(target) {
    this.refreshingGraph = true;
    this._resized.next(target.innerWidth);
  }

  constructor(
    public themeService: ThemeService,
    private featureHelperService: FeatureHelperService,
    private router: Router,
    private clientService: ClientService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private feedbackService: StudentFeedbackService,
    private featureSwitches: FeatureSwitchService,
    private reportService: ReportService,
    @Optional() private route: ActivatedRoute = null
  ) {
    this._resized.pipe(debounceTime(500)).subscribe(() => {
      this.redrawGraphs();
    });
  }

  ngOnInit() {

    let featureList = new Array<string>();
    featureList.push(
      "ViewStudentFeedbackDashboard",
      "SessionOverviewEnhancedSearch");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "ViewStudentFeedbackDashboard")[0].enabled) {
      console.error("User does not have the required permissions to view this dashboard");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
      this.authenticationStateHelper.unauthorizedToken = true;
    }

    this.enhancedSearchPermission = currentUserFeatures.find(f => f.feature == "SessionOverviewEnhancedSearch").enabled;

    this.filterUx = new ScreenFilterUx(this.filtersKey, StudentFeedbackDashboardComponent.filterSpec, this.router, this.route);

    this.featureSwitches.isEnabled(this.filtersKey).subscribe(enabled => {
      this.filtersEnabled = enabled;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = enabled ? this.filterUx.initialState() : null;
      this.applyPendingFilterState();
    });

    if (this.enhancedSearchPermission) {
      this.selectedFilterDateRange = "7days";
      this.populateClientDropdown();
    }

  }

  public redrawGraphs(): void {
    let holder = this.feedbackSurveys.slice();
    this.feedbackSurveys = new Array<FeedbackDashboardItem>();
    setTimeout(() => {
      this.feedbackSurveys = holder;
      this.refreshingGraph = false;
    }, 100);
  }

  public getFeedbackData(): void {
    this.initialSearchClicked = true;
    if (!this.searchAllClients && (this.selectedClient === null || this.selectedClient === undefined)) {
      return;
    }

    let search = new SearchFeedbackDashboardData();
    search.allClients = this.searchAllClients;
    search.clientIds = this.selectedClient != null ? [this.selectedClient.clientId] : [];
    // The switched filters travel only when filters.feedback-dashboard is on.
    if (this.filtersEnabled) {
      search.companyIds = this.companyIds;
      search.surveyType = this.surveyType || null;
      search.tutorId = this.searchAllClients ? null : this.tutorId;
    }

    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }

    let filterDates = this.getFilterDates();

    search.dateFrom = filterDates.fromDate;
    search.dateTo = filterDates.toDate;

    this.feedbackService.getFeedbackDashboardData(search).subscribe((data: FeedbackDashboard) => {

      this.feedbackSurveys = new Array<FeedbackDashboardItem>();

      if (data.feedbackDashboardHeaderData) {
        this.feedbackSurveys.push(...data.feedbackDashboardHeaderData);
      }
        
      if (data.feedbackDashboardItemData) {
        this.feedbackSurveys.push(...data.feedbackDashboardItemData);
      }
      
    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  /**
   * A new client resets the tutor filter and, with the switch on, loads that client's tutors (from the
   * placement cache, the same source as the reports tutor filter).
   */
  public onClientChanged(client: ClientListing): void {
    this.tutorId = null;
    this.tutorList = [];
    if (!this.filtersEnabled || client == null) {
      return;
    }
    this.reportService.GetTutorsForReport({ clientIds: [client.clientId] }).subscribe({
      next: (tutors: ReportTutorFilter[]) => this.tutorList = tutors ?? [],
      error: () => this.tutorList = []
    });
  }

  /** Clear all: back to the dashboard's defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    this.selectedClient = null;
    this.searchAllClients = false;
    this.companyIds = [];
    this.surveyType = '';
    this.tutorId = null;
    this.tutorList = [];
    this.selectedFilterDateRange = '7days';
    this.fromDate = StudentFeedbackDashboardComponent.defaultFromDate();
    this.toDate = StudentFeedbackDashboardComponent.defaultToDate();
    this.fromDateAfterToDate = false;
    this.initialSearchClicked = false;
    this.feedbackSurveys = new Array<FeedbackDashboardItem>();
    this.filterUx.clear();
  }

  private filterState(): FilterState {
    const custom = this.selectedFilterDateRange === 'custom';
    return {
      client: this.selectedClient?.clientId ?? null,
      all: this.searchAllClients || null,
      brand: this.companyIds,
      surveyType: this.surveyType,
      tutor: this.tutorId,
      range: this.selectedFilterDateRange,
      from: custom ? this.fromDate : null,
      to: custom ? this.toDate : null
    };
  }

  /**
   * Restores remembered or linked filters. The client is an object from the client list, so this waits
   * for the list; the tutor is set after the client, because choosing a client resets it. The dashboard
   * then loads, as it would after a Search.
   */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || (this.enhancedSearchPermission && !this.clientsLoaded)) {
      return;
    }
    this.pendingFilterState = null;

    this.searchAllClients = state.all === true;
    this.companyIds = (state.brand as number[]) ?? [];
    this.surveyType = (state.surveyType as string) ?? '';
    if (['7days', '30days', '60days', 'custom'].includes(state.range as string)) {
      this.selectedFilterDateRange = state.range as string;
    }
    if (this.selectedFilterDateRange === 'custom') {
      this.fromDate = (state.from as string) ?? this.fromDate;
      this.toDate = (state.to as string) ?? this.toDate;
      this.validateDates();
    }
    const client = this.clientList.find(c => c.clientId === state.client);
    if (client) {
      this.selectedClient = client;
      this.onClientChanged(client);
    }
    this.tutorId = client ? ((state.tutor as number) ?? null) : null;

    if (this.searchAllClients || client) {
      this.getFeedbackData();
    }
  }

  public filterDateRangeChange(dateFilter: string): void {
    let tempHolder = this.selectedFilterDateRange;
    this.selectedFilterDateRange = dateFilter;
    if (dateFilter != 'custom') {
      this.getFeedbackData();
      if (this.showClientValidationError) {
        this.selectedFilterDateRange = tempHolder;
      }
    }
  }

  public validateDates(): void {
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.fromDateAfterToDate = true;
    } else {
      this.fromDateAfterToDate = false;
    }
  }

  private populateClientDropdown(cancelDisableLoading: boolean = false): void {

    this.clientService.getClientListForAddStudent().subscribe((response: ClientListForAddStudentResponse) => {
      if (response.success) {
        this.clientList = response.clients;
        this.clientList.forEach(c => c.dropdownDisplay = c.clientName + " (" + c.clientId + ")");
        this.filteredClientList = response.clients;
        this.clientsLoaded = true;
        this.applyPendingFilterState();
      } else if (response.responseMessage.toLowerCase() == "no valid client roles assigned") {
        this.showClientAccessError = true;
      } else {
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", { isValidationError: false });
      }


    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  private getFilterDates(): any {
    var today = new Date();
    today.setHours(6, 0, 0, 0);

    let todayHolder = new Date();
    todayHolder.setHours(6, 0, 0, 0);
    
    switch (this.selectedFilterDateRange) {
      case '7days':
        let last7Days = new Date(todayHolder.setDate(todayHolder.getDate() - 7));
        last7Days.setHours(6, 0, 0, 0);
        return {
          fromDate: last7Days,
          toDate: today
        };
    
      case '30days':
        let last30Days = new Date(todayHolder.setDate(todayHolder.getDate() - 30));
        last30Days.setHours(6, 0, 0, 0);
        return {
          fromDate: last30Days,
          toDate: today
        };
    
      case '60days':
        let last60Days = new Date(todayHolder.setDate(todayHolder.getDate() - 60));
        last60Days.setHours(6, 0, 0, 0);
        return {
          fromDate: last60Days,
          toDate: today
        };
    
      case 'custom': {
        // fromDate/toDate are yyyy-MM-dd strings from the native date inputs.
        const customFrom = new Date(this.fromDate);
        customFrom.setHours(6, 0, 0, 0);
        const customTo = new Date(this.toDate);
        customTo.setHours(23, 59, 59, 999);
        return {
          fromDate: customFrom,
          toDate: customTo
        };
      }
    
      default:
        break;
    }
  }

}
